'use client';

import type { JobTemplate } from '@repo/api-gw-sdk';

import { Wizard } from '@/components/wizard/Wizard';

import { CreateJobTemplateFlow } from './CreateJobTemplateFlow';
import type { CreateJobTemplateWizardState } from './createJobTemplateWizardState';

interface UpdateJobTemplateWizardPanelProps {
  workspaceId: string;
  workspaceCloud: string;
  template: JobTemplate;
  onAbort: () => void;
  onUpdated: () => void;
}

const templateToWizardState = (
  template: JobTemplate,
  workspaceId: string,
  workspaceCloud: string
): CreateJobTemplateWizardState => {
  return {
    workspaceId,
    workspaceCloud,
    name: template.name,
    description: template.description ?? '',
    entryPoint: template.sparkSubmitParams?.entryPoint ?? '',
    entryPointArguments:
      template.sparkSubmitParams?.entryPointArguments?.join(' ') ?? '',
    sparkConfig: template.sparkSubmitParams?.sparkSubmitParameters
      ? Object.entries(template.sparkSubmitParams.sparkSubmitParameters).map(
          ([key, value]) => ({ key, value })
        )
      : [{ key: '', value: '' }],
    environmentVariables: template.sparkSubmitParams?.environmentVariables
      ? Object.entries(template.sparkSubmitParams.environmentVariables).map(
          ([key, value]) => ({ key, value })
        )
      : [{ key: '', value: '' }],
    retryEnabled: !!template.retryPolicy,
    maxRetries: template.retryPolicy?.maxAttempts ?? 3,
    cooldownInterval: template.retryPolicy?.cooldownInterval ?? 60,
    jobTimeout: template.jobTimeout?.toString() ?? '',
    driverDiskSize: template.storageConfig?.driverDiskSize?.toString() ?? '',
    executorDiskSize:
      template.storageConfig?.executorDiskSize?.toString() ?? '',
    eonSparkVersionsEnabled:
      !!template.eonSparkVersions && template.eonSparkVersions.length > 0,
    eonSparkVersions: template.eonSparkVersions ?? [],
    isUpdateMode: true,
    templateId: template.id,
  };
};

export const UpdateJobTemplateWizardPanel = ({
  workspaceId,
  workspaceCloud,
  template,
  onAbort,
  onUpdated,
}: UpdateJobTemplateWizardPanelProps) => {
  return (
    <Wizard<CreateJobTemplateWizardState>
      onAbort={onAbort}
      hideBackArrow={true}
      onFinish={async () => {
        onUpdated();
        return true;
      }}
      flow={[CreateJobTemplateFlow]}
      initialState={templateToWizardState(
        template,
        workspaceId,
        workspaceCloud
      )}
      title='Update Job Template'
      stepperLabels={[]}
    />
  );
};
